import { useEffect, useRef, useCallback } from 'react';
import { fetchSSE } from '@/utils/fetchSSE';

export interface GradingResult {
  questionId: string;
  correctOptionIds: string[];
  actualValues: Record<string, number>;
  metadata?: {
    fixtureStatus?: string;
    gradedAt?: string;
    statType?: string;
    threshold?: number;
  };
  timestamp: string;
}

interface UsePickemLiveScoringOptions {
  pickemId: string | null;
  enabled?: boolean;
  onError?: (error: string) => void;
}

interface SSEMessage {
  type: 'connected' | 'grading_result';
  pickemId?: string;
  questionId?: string;
  questionIds?: string[];
  data?: GradingResult;
}

/**
 * External store backing the live-scoring stream.
 *
 * Grading results live here in a plain Map rather than React state, so an
 * incoming update for one question notifies only the listeners subscribed to
 * that question. Consumers subscribe via {@link useSyncExternalStore} (see
 * `useQuestionGradingResult`), which keeps re-renders proportional to the number
 * of questions that actually changed instead of fanning out to every consumer of
 * the provider on each message.
 */
export interface LiveScoringStore {
  /** Latest grading result for a question, or null if none has streamed yet. */
  getResult: (questionId: string) => GradingResult | null;
  /** Subscribe to updates for a single question. Returns an unsubscribe fn. */
  subscribeQuestion: (questionId: string, listener: () => void) => () => void;
  /** Current connection state. */
  getIsConnected: () => boolean;
  /** Subscribe to connection-state changes. Returns an unsubscribe fn. */
  subscribeConnection: (listener: () => void) => () => void;
}

function createLiveScoringStore() {
  const results = new Map<string, GradingResult>();
  const questionListeners = new Map<string, Set<() => void>>();
  const connectionListeners = new Set<() => void>();
  let connected = false;

  const setResult = (questionId: string, result: GradingResult) => {
    results.set(questionId, result);
    const listeners = questionListeners.get(questionId);
    if (listeners) {
      listeners.forEach(listener => listener());
    }
  };

  const setConnected = (value: boolean) => {
    if (connected === value) return;
    connected = value;
    connectionListeners.forEach(listener => listener());
  };

  const store: LiveScoringStore = {
    getResult: questionId => results.get(questionId) ?? null,
    subscribeQuestion: (questionId, listener) => {
      let listeners = questionListeners.get(questionId);
      if (!listeners) {
        listeners = new Set();
        questionListeners.set(questionId, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners?.delete(listener);
        if (listeners?.size === 0) {
          questionListeners.delete(questionId);
        }
      };
    },
    getIsConnected: () => connected,
    subscribeConnection: listener => {
      connectionListeners.add(listener);
      return () => {
        connectionListeners.delete(listener);
      };
    },
  };

  return { store, setResult, setConnected };
}

/**
 * Opens a SINGLE SSE connection that streams grading updates for every question
 * in a pickem, replacing the previous one-connection-per-question approach.
 *
 * Results are kept in an external store (see {@link LiveScoringStore}) rather
 * than React state, so each rendered question can subscribe to just its own
 * updates and only that question re-renders when its grading result changes.
 */
export function usePickemLiveScoring({
  pickemId,
  enabled = true,
  onError,
}: UsePickemLiveScoringOptions) {
  // The store is created once and kept stable for the lifetime of the hook so
  // that the context value never changes identity and consumers can rely on it.
  const storeRef = useRef<ReturnType<typeof createLiveScoringStore> | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createLiveScoringStore();
  }
  const { store, setResult, setConnected } = storeRef.current;

  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onErrorRef = useRef(onError);
  const MAX_RECONNECT_ATTEMPTS = 5;

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const disconnect = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnected(false);
  }, [setConnected]);

  const connect = useCallback(() => {
    if (!pickemId || !enabled) return;

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const url = `${apiUrl}/api/admin/live-scoring/pickem/${pickemId}/stream`;

    const abort = new AbortController();
    abortRef.current = abort;

    const handleConnectionLost = (reason: string) => {
      if (abort.signal.aborted) return;
      setConnected(false);

      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 10000);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error('[PickemLiveScoringSSE] Max reconnect attempts reached:', reason);
        if (onErrorRef.current) {
          onErrorRef.current('Connection lost. Please refresh the page.');
        }
      }
    };

    setConnected(true);
    reconnectAttemptsRef.current = 0;

    fetchSSE(
      url,
      data => {
        try {
          const message = data as SSEMessage;
          if (message.type === 'connected') {
            // connection confirmed
          } else if (message.type === 'grading_result' && message.questionId && message.data) {
            setResult(message.questionId, message.data);
          }
        } catch (error) {
          console.error('[PickemLiveScoringSSE] Error handling message:', error);
        }
      },
      abort.signal
    )
      .then(() => {
        handleConnectionLost('stream ended');
      })
      .catch(err => {
        if (abort.signal.aborted) return;
        console.error('[PickemLiveScoringSSE] Connection error:', err);
        handleConnectionLost(err instanceof Error ? err.message : 'unknown error');
      });
  }, [pickemId, enabled, setConnected, setResult]);

  useEffect(() => {
    if (enabled && pickemId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, pickemId, connect, disconnect]);

  return {
    store,
    disconnect,
  };
}
